import React, { useEffect, useMemo, useRef } from 'react';
import _ from 'lodash';
import DocumentTemplateUtils from 'services/DocumentTemplateUtils';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import DefaultTextField from 'components/Utils/DefaultTextField';
import './style.scss';
import DocumentTemplateTextfield from 'components/HealthRecords/Documents/DocumentTemplate/DocumentTemplateTextfield';
import CPPUtils from 'services/CPPUtils';
import MenuItem from '@material-ui/core/MenuItem';
import { api } from 'careand-redux/api/ApiThunks';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import PerformanceUtils from 'services/PerformanceUtils';
import { Parser } from 'expr-eval-fork';
import { FeeFieldPricingFields } from 'components/HealthRecords/Documents/DocumentTemplate/Utils/PricingFields';

export const SmartInputOptions = (props) => (
  <>
    <DefaultSelectField
      label="SmartType"
      width="250px"
      name="smartType"
      disabled={props.editElement === undefined}
      value={props.editElement.smartType}
      onChange={(event) => props.updateField(props.id, { smartType: event.target.value })}
      inputs={DocumentTemplateUtils.getSmartInputTypes()}
    />
    <DefaultTextField
      label="Metadata"
      name="metadata"
      value={props.editElement.metadata}
      onChange={(event) => props.updateField(props.id, { metadata: event.target.value })}
      disabled={!props.editElement}
    />
    {props.editElement.smartType === 'fees' && (
      <FeeFieldPricingFields element={props.editElement} updateField={props.updateField} />
    )}
    {props.editElement.smartType === 'growth_chart' && <GrowthChartOptions {...props} />}
    {props.editElement.smartType === 'bmi' && <BMIOptions {...props} />}
    {props.editElement.smartType === 'metric' && <MetricOptions {...props} />}
    {props.editElement.smartType === 'checkbox_sum' && <CheckboxSumOptions {...props} />}
    {props.editElement.smartType === 'formula' && <FormulaOptions {...props} />}
  </>
);

const GrowthChartOptions = (props) => (
  <>
    <DefaultTextField
      name="smartMinX"
      label="Min X"
      value={props.editElement.smartMinX}
      style={{ width: '45%', marginRight: '10' }}
      onChange={(event) => props.updateField(props.id, { smartMinX: event.target.value })}
    />
    <DefaultTextField
      name="smartMaxX"
      label="Max X"
      value={props.editElement.smartMaxX}
      style={{ width: '45%' }}
      onChange={(event) => props.updateField(props.id, { smartMaxX: event.target.value })}
    />
    <DefaultTextField
      name="smartMinY"
      label="Min Y"
      value={props.editElement.smartMinY}
      style={{ width: '45%', marginRight: '10' }}
      onChange={(event) => props.updateField(props.id, { smartMinY: event.target.value })}
    />
    <DefaultTextField
      name="smartMaxY"
      label="Max Y"
      value={props.editElement.smartMaxY}
      style={{ width: '45%' }}
      onChange={(event) => props.updateField(props.id, { smartMaxY: event.target.value })}
    />
    <DefaultTextField
      name="smartXFields"
      label="X Fields"
      value={props.editElement.smartXFields}
      onChange={(event) => props.updateField(props.id, { smartXFields: event.target.value })}
    />
    <DefaultTextField
      name="smartYFields"
      label="Y Fields"
      value={props.editElement.smartYFields}
      onChange={(event) => props.updateField(props.id, { smartYFields: event.target.value })}
    />
  </>
);

export const GrowthChartSmartInput = (props) => {
  let { field, fieldValues, editMode, selectedEditElement } = props;
  let { smartMinX, smartMinY, smartMaxX, smartMaxY, smartXFields, smartYFields, id } = field;

  smartMinX = parseFloat(smartMinX);
  smartMinY = parseFloat(smartMinY);
  smartMaxX = parseFloat(smartMaxX);
  smartMaxY = parseFloat(smartMaxY);

  let points = [];

  if (editMode) {
    points = [
      [smartMinX, smartMinY],
      [smartMaxX, smartMinY],
      [smartMinX, smartMaxY],
      [smartMaxX, smartMaxY],
      [(smartMaxX - smartMinX) / 2 + smartMinX, (smartMaxY - smartMinY) / 2 + smartMinY],
    ];
  }

  let d = {};

  if (smartXFields && smartYFields) {
    let newPoints = {};
    smartXFields = smartXFields.split(',');
    smartYFields = smartYFields.split(',');
    smartXFields.forEach((id, key) => {
      let strVal = fieldValues[id]?.toLowerCase();
      let value = parseFloat(strVal);
      if (strVal && (strVal.includes('year') || strVal.includes('yo'))) value *= 12;
      else if (strVal && strVal.includes('d')) value /= 30;
      if (!!strVal) {
        newPoints[key] = [value];
        d[key] = { x: id };
      }
    });
    smartYFields.forEach((id, key) => {
      let strVal = fieldValues[id]?.toLowerCase();
      let value = parseFloat(strVal);
      if (strVal && (strVal.includes('year') || strVal.includes('yo'))) value *= 12;
      else if (strVal && strVal.includes('d')) value /= 30;
      if (!!strVal && newPoints[key]) {
        d[key].y = id;
        newPoints[key].push(value);
      }
    });
    if (Object.keys(newPoints).length > 0) points = Object.values(newPoints);
  }

  points = points.filter((point) => point.length === 2);

  function getPercentage(val, min, max) {
    let range = max - min;
    let shiftedVal = val - min;
    return (shiftedVal / range) * 100.0;
  }

  let className = 'template-field growth-chart-smart-input';
  if (selectedEditElement === id) className += ' edit';

  return (
    <div data-field-id={id} className={className} style={GROWTH_STYLE}>
      {points.map((point, key) => {
        let point0 = point[0];
        let point1 = point[1];

        let left = getPercentage(point0, smartMinX, smartMaxX) + '%';
        let bottom = getPercentage(point1, smartMinY, smartMaxY) + '%';
        return <div key={key} className="point" style={{ ...GROWTH_POINT_STYLE, left, bottom }} />;
      })}
    </div>
  );
};

const MetricOptions = (props) => {
  const dispatch = useAppDispatch();
  const metricTypes = useAppSelector((state) => state.healthRecords.metricTypes);
  const orderedMetricTypes = useMemo(() => {
    return PerformanceUtils.sortBy(metricTypes, 'name', false);
  }, [metricTypes]);

  useEffect(() => {
    dispatch(api.HealthRecords.getMetricTypes());
  }, [dispatch]);

  return (
    <>
      <DefaultSelectField
        id="metric-field"
        label="Metric"
        name="metricId"
        inputElements={orderedMetricTypes.map((metric) => (
          <MenuItem key={metric.id} value={metric.id}>
            {metric.name}
          </MenuItem>
        ))}
        value={props.editElement.metricId}
        onChange={(event) => props.updateField(props.id, { metricId: event.target.value })}
      />
    </>
  );
};

const BMIOptions = (props) => (
  <>
    <DefaultTextField
      name="smartWeightPos"
      label="Relative Position (Weight)"
      value={props.editElement.smartWeightPos}
      onChange={(event) => props.updateField(props.id, { smartWeightPos: event.target.value })}
    />
    <DefaultTextField
      name="smartHeightPos"
      label="Relative Position (Height)"
      value={props.editElement.smartHeightPos}
      onChange={(event) => props.updateField(props.id, { smartHeightPos: event.target.value })}
    />
  </>
);

const CheckboxSumOptions = (props) => (
  <>
    <DefaultTextField
      name="elementTag"
      label="Element Tag"
      value={props.editElement.elementTag}
      onChange={(event) => props.updateField(props.id, { elementTag: event.target.value })}
    />
  </>
);

const FormulaOptions = (props) => (
  <>
    <DefaultTextField
      name="formula"
      label="Formula"
      value={props.editElement.formula}
      onChange={(event) => props.updateField(props.id, { formula: event.target.value })}
      placeholder="e.g., field1 + field2 * 2"
    />
  </>
);

export const MetricSmartInput = (props) => {
  let { field, fieldValues } = props;
  let { metricId, id } = field;

  let value = fieldValues[id];

  useEffect(() => {
    if (value) {
      props.change('metricId', metricId);
      props.change('metricValue', value);
    }
  }, [metricId, value]);

  return <DocumentTemplateTextfield {...props} />;
};

export const BMISmartInput = (props) => {
  useEffect(() => {
    if (!props.field.disabled) {
      props.updateField(props.field.id, { disabled: 'true' });
    }
  }, [props.field.disabled, props.updateField]);

  let { field, fieldValues, inputs } = props;
  let { smartHeightPos, smartWeightPos } = field;

  smartHeightPos = parseInt(smartHeightPos);
  smartWeightPos = parseInt(smartWeightPos);

  let currentPos = 0;
  inputs.forEach((input, index) => {
    if (input.id === field.id) currentPos = index;
  });

  let heightInput = inputs[currentPos + smartHeightPos] || {};
  let weightInput = inputs[currentPos + smartWeightPos] || {};

  let height = fieldValues[heightInput.id];
  let weight = fieldValues[weightInput.id];

  useEffect(() => {
    let w = parseFloat(weight);
    let h = parseFloat(height);

    let value = undefined;

    if (w && h) {
      value = CPPUtils.calculateBMI(h, w);
    } else if (!w || !h) {
      value = '';
    }

    if (value !== undefined) {
      let event = {
        target: {
          name: field.id,
          value,
        },
      };
      props.onChange(event);
    }
  }, [height, weight]);

  return <DocumentTemplateTextfield {...props} />;
};

export const FeeSmartInput = (props) => {
  let { field: originalField, fieldValues, inputs, prefill } = props;

  let newStyle = DocumentTemplateUtils.parseStyle(originalField.style || {}) || {};
  newStyle = { ...newStyle, display: 'none' };
  const field = { ...originalField, style: JSON.stringify(newStyle) };

  let fixedFee = 0;
  let fee_hst = false;

  try {
    let metadata = JSON.parse(field.metadata || '{}');
    fixedFee = metadata.fixed_fee || 0;
    fee_hst = metadata.hst || fee_hst;
  } catch (err) {}
  let { fees, fee_lines } = useMemo(() => {
    let keys = [];
    let fee_lines = [];
    inputs.forEach((input) => {
      let key = input.id;
      let value = fieldValues[key];
      let placeholder = input?.placeholder;
      if (value !== undefined) {
        value = value === true || value === 'on';
      } else if (placeholder && prefill && prefill[placeholder] !== undefined) {
        value = prefill[placeholder] === true;
      }
      if (value) {
        keys.push(key);
      }
    });
    let filtered = inputs.filter((input) => keys.includes(input.id));
    let fees = 0;
    filtered.forEach((input) => {
      if (input.metadata) {
        try {
          let metadata = JSON.parse(input.metadata);

          // `inputs` is the whole page, not just the fee checkboxes, so inputs
          // whose metadata serves another feature (checkbox_sum tags, etc.)
          // arrive here too. Without this guard their absent fee turns the
          // total into NaN, which reaches the server as a null fee and makes
          // the document impossible to charge. Metadata is hand-authored JSON,
          // so accept a quoted number as well.
          let fee = typeof metadata.fee === 'string' ? parseFloat(metadata.fee) : metadata.fee;
          if (typeof fee !== 'number' || !Number.isFinite(fee)) return;

          fees += fee;

          fee_lines = fee_lines || [];
          fee_lines.push({
            description: metadata.description || input.id,
            amount: fee,
            // A checkbox opts in or out of HST on its own; without its own
            // flag it inherits the fee field's document-wide hst setting.
            taxable: metadata.hst ?? fee_hst,
          });
        } catch (err) {
          console.log(err);
        }
      }
    });

    if (fixedFee) {
      fee_lines.push({ description: 'Processing Fee', amount: fixedFee, taxable: fee_hst });
      fees += fixedFee;
    }

    return { fees: `$${parseFloat(fees).toFixed(2)}`, fee_lines };
  }, [fieldValues, inputs, fixedFee, fee_hst, prefill]);

  const prevFeesRef = useRef(fees);
  const prevFeeHstRef = useRef(fee_hst);
  const prevFeeLinesRef = useRef(fee_lines);

  useEffect(() => {
    const feeLinesChanged = JSON.stringify(fee_lines) !== JSON.stringify(prevFeeLinesRef.current);
    if (fees === prevFeesRef.current && fee_hst === prevFeeHstRef.current && !feeLinesChanged) {
      return;
    }
    prevFeesRef.current = fees;
    prevFeeHstRef.current = fee_hst;
    prevFeeLinesRef.current = fee_lines;

    let event = {
      target: {
        name: field.id,
        value: fees,
      },
    };
    props.onChange(event);
    props.change('fee', fees);
    props.change('fee_hst', fee_hst);
    props.change('fee_lines', fee_lines);
  }, [fees, fee_hst, fee_lines, field.id]);

  return <DocumentTemplateTextfield {...props} field={field} className="fee-smart-input" />;
};

export const CheckboxSumSmartInput = (props) => {
  let { field, fieldValues, inputs } = props;
  let { elementTag } = field;

  const sum = useMemo(() => {
    if (!elementTag || !inputs) return 0;

    let total = 0;
    inputs.forEach((input) => {
      const isChecked = fieldValues[input.id] === 'on' || fieldValues[input.id] === true;
      if (isChecked && input.metadata) {
        try {
          const metadata = JSON.parse(input.metadata);
          if (Array.isArray(metadata.tag) && metadata.tag.includes(elementTag)) {
            const weight = metadata.weight ?? 1;
            total += parseFloat(weight) ?? 1;
          }
        } catch (err) {
          // Invalid JSON in metadata, skip
        }
      }
    });

    return total;
  }, [elementTag, fieldValues, inputs]);

  const fieldValue = fieldValues[field.id];
  const sumText = sum.toString();
  useEffect(() => {
    if (fieldValue === sumText) {
      return;
    }

    const event = {
      target: {
        name: field.id,
        value: sumText,
      },
    };
    props.onChange(event);
  }, [sumText, field.id, fieldValue, fieldValues]);

  return <DocumentTemplateTextfield {...props} />;
};

export const FormulaSmartInput = (props) => {
  let { field, fieldValues, inputs, prefill } = props;
  let { formula } = field;

  useEffect(() => {
    if (!props.field.disabled) {
      props.updateField(props.field.id, { disabled: 'true' });
    }
  }, [props.field.disabled, props.updateField]);

  const result = useMemo(() => {
    if (!formula) return '';
    try {
      const parser = new Parser();
      const expr = parser.parse(formula);

      // Create a context with functions to access field values
      const stdlib = {
        field: (fieldId) => {
          const placeholder = inputs.find((input) => input.id === fieldId)?.placeholder;
          const value = fieldValues[fieldId] || prefill[placeholder];
          return value === undefined || value === null ? '' : parseFloat(value);
        },
        fieldNum: (fieldId) => {
          const value = fieldValues[fieldId];
          if (value === undefined || value === null) return undefined;
          if (typeof value === 'string' && value === '') return undefined;
          return parseFloat(value);
        },
        sumFieldsWhere: (metadataProperty, value, byKey) => {
          const matches = inputs.filter(
            (it) =>
              typeof it === 'object' &&
              it.metadata &&
              JSON.parse(it.metadata)[metadataProperty] === value,
          );
          const sum = _(matches)
            .map((it) => {
              let value = byKey ? it.metadata[byKey] : fieldValues[it.id];
              value = value === 'on' ? '1' : value === 'off' ? '0' : value;
              return parseInt(value || '0', 10);
            })
            .sum();
          return `${sum}`;
        },
      };

      for (let fnName in stdlib) {
        parser.functions[fnName] = stdlib[fnName];
      }

      const evaluation = expr.evaluate();
      if (isNaN(evaluation)) return 'INVALID VALUE';
      return evaluation.toString();
    } catch (err) {
      return 'INVALID VALUE';
    }
  }, [formula, fieldValues, inputs, prefill]);

  const fieldValue = fieldValues[field.id];
  useEffect(() => {
    if (fieldValue === result) {
      return;
    }

    const event = {
      target: {
        name: field.id,
        value: result,
      },
    };
    props.onChange(event);
  }, [result, field.id, fieldValue, fieldValues]);

  return <DocumentTemplateTextfield {...props} />;
};

export const OtherSmartInput = (props) => (
  <div className="template-field" data-field-id={props.field.id}>
    Other
  </div>
);

const GROWTH_STYLE = {
  position: 'relative',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
};

const GROWTH_POINT_STYLE = {
  position: 'absolute',
  display: 'block',
  width: '10px',
  height: '10px',
  background: 'red',
  borderRadius: '50%',
  marginLeft: '-5px',
  marginBottom: '-5px',
};
